import {
  FeedModalStackContext,
  FeedModalTypes,
} from "../FeedFilter/SearchFeed";
import { FilterDispatchContext } from "../FeedFilter/State/FilterDispatchContext";
import { useGroopitApiContext, useGroupContext } from "groopit-js";
import {
  ErrorDTO,
  GroopitFilterDisplayDTO,
  InsightsReportDTO,
} from "groopit-js/client";
import React, {
  Dispatch,
  ReactNode,
  SetStateAction,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useEffectOnce } from "react-use";
import useLatestRef from "../react/react-use/useLatestRef";
import { canManage } from "./InsightsReport";
import { createFilterReplaceFilterAction } from "../FeedFilter/State/FilterActions";
import { UnnamedGroopitFilterDisplayDTO } from "../FeedFilter/GroopitFilterDTO";

export const useInsightsPanelContext = () => {
  const context = useContext(InsightsPanelContext);
  if (!context) {
    throw "useInsightsPanelContext() invoked without a parent provider";
  }
  return context;
};

interface SelectedInsightReport {
  report: InsightsReportDTO;
  isEditMode: boolean;
  isNewlyCreated: boolean;
}

interface InsightReportGeneratingState {
  filter: GroopitFilterDisplayDTO;
  groopitCount: number;
  reportCompleted: boolean;
  error: ErrorDTO | null;
}

interface InsightsPanelState {
  isGeneratingReport: false | InsightReportGeneratingState;
  setIsGeneratingReport: Dispatch<
    SetStateAction<false | InsightReportGeneratingState>
  >;
  selectedReport: SelectedInsightReport | null;
  setSelectedReport: Dispatch<SetStateAction<SelectedInsightReport | null>>;
  allReports?: InsightsReportDTO[];
  onReportCreated: (report: InsightsReportDTO, isEditMode: boolean) => void;
  onReportUpdated: (report: InsightsReportDTO) => void;
  onReportDeleted: (id: string) => void;
}

export const InsightsPanelContext = createContext<
  InsightsPanelState | undefined
>(undefined);

interface InsightsPanelProviderProps {
  insightsReportId?: string;
  insightsReportEdit: boolean;
  children: ReactNode;
}

export const InsightsPanelProvider = (props: InsightsPanelProviderProps) => {
  const { insightsReportId, insightsReportEdit, children } = {
    ...props,
  };
  const { pushModalStack } = useContext(FeedModalStackContext);
  const filterDispatchContext = useLatestRef(useContext(FilterDispatchContext));
  const { group, user_id, owner } = useGroupContext();
  const api = useLatestRef(useGroopitApiContext());
  const [isGeneratingReport, setIsGeneratingReport] = useState<
    false | InsightReportGeneratingState
  >(false);
  const [selectedReport, setSelectedReport] =
    useState<SelectedInsightReport | null>(null);
  const [allReports, setAllReports] = useState<InsightsReportDTO[]>();

  const onReportCreated = useCallback(
    (report: InsightsReportDTO, isEditMode: boolean) => {
      if (allReports && report) {
        setAllReports([report, ...allReports]);
      }
      setSelectedReport({ report, isEditMode, isNewlyCreated: true });
      pushModalStack?.(FeedModalTypes.Insights);
    },
    [allReports, pushModalStack],
  );

  const onReportUpdated = useCallback(
    (report: InsightsReportDTO) => {
      if (allReports) {
        setAllReports(allReports.map((r) => (r.id === report.id ? report : r)));
      }
    },
    [allReports],
  );

  const onReportDeleted = useCallback(
    (id: string) => {
      if (allReports) {
        setAllReports(allReports.filter((r) => r.id !== id));
      }

      if (
        selectedReport?.report instanceof InsightsReportDTO &&
        selectedReport.report.id === id
      ) {
        setSelectedReport(null);
      }
    },
    [allReports, selectedReport],
  );

  useEffect(() => {
    if (selectedReport) {
      setIsGeneratingReport(false);
    }
  }, [selectedReport]);

  useEffect(() => {
    api.current.insights
      .getInsights(group.id)
      .then(setAllReports)
      .catch((error) => {
        console.error(error);
      });
  }, [api, group.id]);

  useEffectOnce(() => {
    if (insightsReportId && !selectedReport) {
      api.current.insights
        .getInsightsReport(insightsReportId)
        .then((report) => {
          setSelectedReport({
            report,
            isEditMode: insightsReportEdit && canManage(report, user_id, owner),
            isNewlyCreated: false,
          });
          pushModalStack?.(FeedModalTypes.Insights);
        })
        .catch((error) => {
          console.error(error);
        });
    }
  });

  useEffect(() => {
    if (selectedReport) {
      // We only need to handle reports because results are
      // the result of responding to the filters that are already
      // active so we don't need to change them.
      if (selectedReport.report instanceof InsightsReportDTO) {
        filterDispatchContext.current?.filterDispatcher(
          createFilterReplaceFilterAction(
            new UnnamedGroopitFilterDisplayDTO({
              filter: selectedReport.report.filter,
            }),
          ),
        );
      }
    }
  }, [selectedReport, filterDispatchContext]);

  return (
    <InsightsPanelContext.Provider
      value={{
        isGeneratingReport,
        setIsGeneratingReport,
        selectedReport,
        setSelectedReport,
        allReports,
        onReportCreated,
        onReportUpdated,
        onReportDeleted,
      }}
    >
      {children}
    </InsightsPanelContext.Provider>
  );
};
